import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AnalyticsEvent, capture } from '@cctv/analytics';
import { useExperienceRoute } from '@cctv/hooks/useExperienceRoute';
import {
  FamilyFeudAction,
  FamilyFeudActionType,
} from '@cctv/pages/Block/FamilyFeudManager/familyFeudReducer';
import {
  DrawingUpdateMessage,
  ExperienceChannelMessage,
  JsonObject,
  WebSocketMessageTypes,
  isDrawingUpdateMessage,
  isExperiencePayloadMessage,
} from '@cctv/types';
import { qaLogger } from '@cctv/utils';

import { useAdminAuth } from './AdminAuthContext';
import { useAuth } from './AuthContext';
import { useDispatchRegistry } from './DispatchRegistryContext';
import { useExperienceState } from './ExperienceStateContext';
import { DrawingAction, useLobbyDrawingDispatch } from './LobbyDrawingContext';

const ACTION_TYPE_MAP = new Map<string, FamilyFeudActionType>([
  ['bucket_added', FamilyFeudActionType.BUCKET_ADDED],
  ['bucket_renamed', FamilyFeudActionType.BUCKET_RENAMED],
  ['bucket_deleted', FamilyFeudActionType.BUCKET_DELETED],
  ['answer_assigned', FamilyFeudActionType.ANSWER_ASSIGNED],
  ['answer_received', FamilyFeudActionType.ANSWER_RECEIVED],
  ['question_added', FamilyFeudActionType.QUESTION_ADDED],
  ['question_deleted', FamilyFeudActionType.QUESTION_DELETED],
]);

const DRAWING_OPERATIONS: ReadonlySet<string> = new Set<DrawingAction['operation']>([
  'clear_all',
  'avatar_committed',
  'canvas_cleared',
  'stroke_started',
  'stroke_points_appended',
  'stroke_ended',
  'stroke_undone',
  'canvas_clear_undone',
]);

const CHANNEL_MESSAGE_TYPES: ReadonlySet<string> = new Set<ExperienceChannelMessage['type']>([
  ...Object.values(WebSocketMessageTypes),
  'drawing_update',
]);

/** ActionCable envelope: protocol frames carry `type`, broadcasts carry `message`. */
interface ChannelFrame {
  type?: string;
  message?: ExperienceChannelMessage;
}

interface WebSocketConfig {
  label: string;
  identifier: Record<string, string | undefined>;
  onMessage: (frame: ChannelFrame) => void;
  onConnect?: () => void;
  onError?: () => void;
  onClose?: (event: CloseEvent) => void;
}

function createWebSocketConnection(
  config: WebSocketConfig,
  wsRef: { current: WebSocket | undefined },
  identifierRef: { current: string | undefined },
  disposeRef: { current: boolean },
  attempt = 0,
  onReconnecting?: (reconnecting: boolean) => void,
): void {
  if (disposeRef.current) return;

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${window.location.host}/cable`;

  qaLogger(
    `[${config.label}] Connecting: ${wsUrl}${attempt > 0 ? ` (attempt ${attempt + 1})` : ''}`,
  );
  const ws = new WebSocket(wsUrl);
  wsRef.current = ws;
  identifierRef.current = JSON.stringify(config.identifier);

  let rejected = false;

  const onOpen = () => {
    qaLogger(`[${config.label}] WebSocket connected`);
    // Only a reconnect is interesting — the first connect is just startup. This
    // pairs with `websocket disconnected` to show how long participants spent
    // cut off from live updates during a show.
    if (attempt > 0) {
      capture(AnalyticsEvent.WebsocketReconnected, {
        stream: config.label,
        attempts: attempt,
      });
    }
    onReconnecting?.(false);
    config.onConnect?.();

    ws.send(
      JSON.stringify({
        command: 'subscribe',
        identifier: JSON.stringify(config.identifier),
      }),
    );
  };

  const onMessage = (event: MessageEvent) => {
    const frame: ChannelFrame = JSON.parse(event.data);
    if (frame.type === 'reject_subscription') rejected = true;
    config.onMessage(frame);
  };

  const onError = () => {
    qaLogger(`[${config.label}] WebSocket error`);
    config.onError?.();
  };

  const onClose = (event: CloseEvent) => {
    qaLogger(`[${config.label}] WebSocket closed${event.code ? `. Code: ${event.code}` : ''}`);
    config.onClose?.(event);

    if (!disposeRef.current && !rejected && event.code !== 1000 && event.code !== 1001) {
      const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
      capture(AnalyticsEvent.WebsocketDisconnected, {
        stream: config.label,
        close_code: event.code,
        attempt: attempt + 1,
      });
      qaLogger(`[${config.label}] Reconnecting in ${delay}ms (attempt ${attempt + 1})`);
      onReconnecting?.(true);
      setTimeout(
        () =>
          createWebSocketConnection(
            config,
            wsRef,
            identifierRef,
            disposeRef,
            attempt + 1,
            onReconnecting,
          ),
        delay,
      );
    }
  };

  ws.addEventListener('open', onOpen);
  ws.addEventListener('message', onMessage);
  ws.addEventListener('error', onError);
  ws.addEventListener('close', onClose);
}

function isChannelMessage(
  payload: ChannelFrame | ExperienceChannelMessage,
): payload is ExperienceChannelMessage {
  return payload.type !== undefined && CHANNEL_MESSAGE_TYPES.has(payload.type);
}

function isDrawingAction(
  message: DrawingUpdateMessage,
): message is DrawingUpdateMessage & DrawingAction {
  return DRAWING_OPERATIONS.has(message.operation);
}

function parseChannelMessage(raw: ChannelFrame): ExperienceChannelMessage | null {
  if (raw.type === 'ping' || raw.type === 'welcome') return null;
  if (raw.type === 'confirm_subscription' || raw.type === 'reject_subscription') return null;

  const payload = raw.message ?? raw;
  return isChannelMessage(payload) ? payload : null;
}

export interface WebSocketContextType {
  wsConnected: boolean;
  wsError?: string;
  reconnecting: boolean;
  experiencePerform: (
    action: string,
    payload?: JsonObject,
    target?: 'primary' | 'monitor' | 'impersonation',
  ) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { code, isManagePage, isMonitorPage } = useExperienceRoute();
  const { jwt, isLoading } = useAuth();
  const { adminJWT, isAdminLoading } = useAdminAuth();
  const {
    setExperience,
    setParticipant,
    setExperienceStatus,
    setError,
    setMonitorView,
    setParticipantView,
    setWsReady,
    setSubmissionState,
    impersonatedParticipantId,
  } = useExperienceState();
  const { getFamilyFeudDispatch, getBalloonPumpLeaderDispatch } = useDispatchRegistry();
  const lobbyDrawingDispatch = useLobbyDrawingDispatch();

  const [wsConnected, setWsConnected] = useState(false);
  const [wsError, setWsError] = useState<string>();
  const [reconnecting, setReconnecting] = useState(false);

  const wsRef = useRef<WebSocket>(undefined);
  const monitorWsRef = useRef<WebSocket>(undefined);
  const impersonationWsRef = useRef<WebSocket>(undefined);
  const wsIdentifierRef = useRef<string>(undefined);
  const monitorIdentifierRef = useRef<string>(undefined);
  const impersonationIdentifierRef = useRef<string>(undefined);

  const mainDisposeRef = useRef(false);
  const monitorDisposeRef = useRef(false);
  const impersonationDisposeRef = useRef(false);

  const reconnectParticipantWs = useCallback(() => {
    if (!wsRef.current || !wsIdentifierRef.current) return;
    qaLogger('[WS] Resubscribe requested — reconnecting participant websocket');
    const identifier = JSON.parse(wsIdentifierRef.current);

    wsRef.current.close(1000);
    wsRef.current = undefined;
    mainDisposeRef.current = false;
    setReconnecting(true);

    createWebSocketConnection(
      {
        label: 'PARTICIPANT WS',
        identifier,
        onMessage: (msg) => handleMessageRef.current(msg, 'PARTICIPANT WS', 'main'),
        onConnect: () => {
          setWsConnected(true);
          setWsError(undefined);
          setReconnecting(false);
        },
        onError: () => {
          setWsError('Connection error');
          setWsConnected(false);
          setReconnecting(false);
        },
        onClose: () => setWsConnected(false),
      },
      wsRef,
      wsIdentifierRef,
      mainDisposeRef,
    );
  }, []);

  const handleExperienceMessage = useCallback(
    (raw: ChannelFrame, label: string, target: 'main' | 'monitor' | 'impersonation') => {
      if (raw.type === 'ping' || raw.type === 'welcome') return;

      if (raw.type === 'confirm_subscription') {
        qaLogger(`[${label}] Subscription confirmed`);
        return;
      }
      if (raw.type === 'reject_subscription') {
        qaLogger(`[${label}] SUBSCRIPTION REJECTED - Check backend logs`);
        return;
      }

      if (target === 'monitor') {
        const channelMsg = parseChannelMessage(raw);
        if (!channelMsg) return;

        if (isDrawingUpdateMessage(channelMsg)) {
          if (isDrawingAction(channelMsg)) {
            lobbyDrawingDispatch(channelMsg);
          }
        } else if (isExperiencePayloadMessage(channelMsg)) {
          qaLogger(`[${label}] Processing: ${channelMsg.type}`);
          if (channelMsg.experience) {
            qaLogger(
              `[${label}] Updating Monitor view: status=${channelMsg.experience.status}, blocks=${channelMsg.experience.blocks?.length || 0}`,
            );
            setMonitorView(channelMsg.experience);
          }
        }
        return;
      }

      const wsMessage = parseChannelMessage(raw);
      if (!wsMessage || isDrawingUpdateMessage(wsMessage)) return;

      if (wsMessage.type === WebSocketMessageTypes.RESUBSCRIBE_REQUIRED && target === 'main') {
        reconnectParticipantWs();
        return;
      }

      if (isExperiencePayloadMessage(wsMessage)) {
        qaLogger(`[${label}] Processing: ${wsMessage.type}`);
        const updatedExperience = wsMessage.experience;

        if (updatedExperience) {
          qaLogger(
            `[${label}] Updating experience: status=${updatedExperience.status}, blocks=${updatedExperience.blocks?.length || 0}`,
          );

          if (target === 'impersonation') {
            setParticipantView(updatedExperience);
          } else {
            setExperience(updatedExperience);
            setExperienceStatus(updatedExperience.status === 'live' ? 'live' : 'lobby');
            setError(undefined);
            setWsReady(true);

            if (wsMessage.participant) {
              setParticipant(wsMessage.participant);
            }
          }
        }
      } else if (
        target === 'main' &&
        wsMessage.type === WebSocketMessageTypes.FAMILY_FEUD_UPDATED
      ) {
        const { block_id, operation, data } = wsMessage;
        qaLogger(`[${label}] Processing family_feud_updated: ${operation}`);

        const dispatch = getFamilyFeudDispatch(block_id);
        if (dispatch) {
          const actionType = ACTION_TYPE_MAP.get(operation);
          if (actionType) {
            // SAFETY: ACTION_TYPE_MAP.get returned a value, so `operation` is one of the seven
            // family_feud operations, and WebsocketMessageService.family_feud_updated pairs each
            // of those operations with the payload its FamilyFeudAction variant declares. Only
            // that correlation is asserted; both values come from the same broadcast.
            dispatch({ type: actionType, payload: data } as FamilyFeudAction);
          }
        }
      } else if (target === 'main' && wsMessage.type === WebSocketMessageTypes.SUBMISSION_STATE) {
        setSubmissionState(wsMessage.submissions);
      } else if (
        target === 'main' &&
        wsMessage.type === WebSocketMessageTypes.MINIGAME_BALLOON_PUMP_LEADER_UPDATED
      ) {
        const { block_id, leader_fill } = wsMessage;
        getBalloonPumpLeaderDispatch(block_id)?.({ leader_fill });
      }
    },
    [
      setExperience,
      setParticipant,
      setExperienceStatus,
      setError,
      setMonitorView,
      setParticipantView,
      setWsReady,
      setSubmissionState,
      getFamilyFeudDispatch,
      getBalloonPumpLeaderDispatch,
      lobbyDrawingDispatch,
      reconnectParticipantWs,
    ],
  );

  const handleMessageRef = useRef(handleExperienceMessage);
  useEffect(() => {
    handleMessageRef.current = handleExperienceMessage;
  });

  // Main effect: manages admin, monitor, and participant connections.
  // Does not depend on impersonatedParticipantId so impersonation changes
  // don't tear down and rebuild the stable connections.
  useEffect(() => {
    if (!code || isLoading || (isManagePage && isAdminLoading)) {
      qaLogger('[WS SETUP] No code or auth loading, skipping websocket setup');
      return;
    }

    mainDisposeRef.current = false;
    monitorDisposeRef.current = false;

    if (isManagePage) {
      qaLogger('[WS SETUP] MANAGE PAGE - Creating admin and monitor websockets');

      createWebSocketConnection(
        {
          label: 'ADMIN WS',
          identifier: {
            channel: 'ExperienceSubscriptionChannel',
            code,
            token: (adminJWT ?? jwt) || undefined,
          },
          onMessage: (msg) => handleMessageRef.current(msg, 'ADMIN WS', 'main'),
          onConnect: () => {
            setWsConnected(true);
            setWsError(undefined);
          },
          onError: () => {
            setWsError('Connection error');
            setWsConnected(false);
          },
          onClose: () => setWsConnected(false),
        },
        wsRef,
        wsIdentifierRef,
        mainDisposeRef,
      );

      const monitorId = {
        channel: 'ExperienceSubscriptionChannel',
        code,
        view_type: 'monitor',
      };
      createWebSocketConnection(
        {
          label: 'Monitor WS',
          identifier: monitorId,
          onMessage: (msg) => handleMessageRef.current(msg, 'Monitor WS', 'monitor'),
        },
        monitorWsRef,
        monitorIdentifierRef,
        monitorDisposeRef,
      );
    } else if (isMonitorPage) {
      qaLogger('[WS SETUP] MONITOR PAGE - Creating monitor websocket');

      const monitorId = {
        channel: 'ExperienceSubscriptionChannel',
        code,
        view_type: 'monitor',
      };
      createWebSocketConnection(
        {
          label: 'Monitor WS',
          identifier: monitorId,
          onMessage: (msg) => handleMessageRef.current(msg, 'Monitor WS', 'monitor'),
          onConnect: () => {
            setWsConnected(true);
            setWsError(undefined);
          },
          onError: () => {
            setWsError('Connection error');
            setWsConnected(false);
          },
          onClose: () => setWsConnected(false),
        },
        monitorWsRef,
        monitorIdentifierRef,
        monitorDisposeRef,
      );
    } else {
      qaLogger('[WS SETUP] PARTICIPANT PAGE - Creating participant websocket');

      if (jwt) {
        const participantId = { channel: 'ExperienceSubscriptionChannel', code, token: jwt };
        createWebSocketConnection(
          {
            label: 'PARTICIPANT WS',
            identifier: participantId,
            onMessage: (msg) => handleMessageRef.current(msg, 'PARTICIPANT WS', 'main'),
            onConnect: () => {
              setWsConnected(true);
              setWsError(undefined);
            },
            onError: () => {
              setWsError('Connection error');
              setWsConnected(false);
            },
            onClose: () => setWsConnected(false),
          },
          wsRef,
          wsIdentifierRef,
          mainDisposeRef,
          0,
          setReconnecting,
        );
      }
    }

    return () => {
      mainDisposeRef.current = true;
      monitorDisposeRef.current = true;

      if (wsRef.current) {
        qaLogger('[WS] Disconnecting main WebSocket');
        wsRef.current.close(1000);
        wsRef.current = undefined;
      }
      if (monitorWsRef.current) {
        qaLogger('[WS] Disconnecting monitor WebSocket');
        monitorWsRef.current.close(1000);
        monitorWsRef.current = undefined;
      }

      setWsConnected(false);
      setWsError(undefined);
      setWsReady(false);
    };
  }, [adminJWT, jwt, code, isLoading, isAdminLoading, isManagePage, isMonitorPage, setWsReady]);

  // Impersonation effect: manages the impersonation connection independently.
  // Changing the impersonated participant only affects this connection.
  useEffect(() => {
    if (!code || isLoading || !isManagePage) return;

    if (impersonatedParticipantId) {
      qaLogger(`[IMPERSONATION WS] Connecting for participant ${impersonatedParticipantId}`);
      impersonationDisposeRef.current = false;

      if (impersonationWsRef.current) {
        impersonationWsRef.current.close(1000);
      }

      const impersonationId = {
        channel: 'ExperienceSubscriptionChannel',
        code,
        token: (adminJWT ?? jwt) || undefined,
        as_participant_id: impersonatedParticipantId,
      };
      createWebSocketConnection(
        {
          label: 'IMPERSONATION WS',
          identifier: impersonationId,
          onMessage: (msg) => handleMessageRef.current(msg, 'IMPERSONATION WS', 'impersonation'),
        },
        impersonationWsRef,
        impersonationIdentifierRef,
        impersonationDisposeRef,
      );
    }

    return () => {
      impersonationDisposeRef.current = true;
      if (impersonationWsRef.current) {
        qaLogger('[IMPERSONATION WS] Disconnecting');
        impersonationWsRef.current.close(1000);
        impersonationWsRef.current = undefined;
        impersonationIdentifierRef.current = undefined;
      }
      setParticipantView(undefined);
    };
  }, [adminJWT, jwt, code, isLoading, isManagePage, impersonatedParticipantId, setParticipantView]);

  const experiencePerform = useCallback(
    (
      action: string,
      payload?: JsonObject,
      target: 'primary' | 'monitor' | 'impersonation' = 'primary',
    ) => {
      const frames = {
        primary: { sock: wsRef.current, id: wsIdentifierRef.current },
        monitor: { sock: monitorWsRef.current, id: monitorIdentifierRef.current },
        impersonation: { sock: impersonationWsRef.current, id: impersonationIdentifierRef.current },
      } as const;
      const f = frames[target];
      if (!f.sock || !f.id) return;
      const data = JSON.stringify({ action, ...payload });
      f.sock.send(JSON.stringify({ command: 'message', identifier: f.id, data }));
    },
    [],
  );

  const value = useMemo<WebSocketContextType>(
    () => ({ wsConnected, wsError, reconnecting, experiencePerform }),
    [wsConnected, wsError, reconnecting, experiencePerform],
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
